/**
 * Admin 라우트 admin.site.works.* 와 동기화.
 *
 * 편집/생성 페이지에서:
 *  - 템플릿 <select> 변경 시 /console/site/works/{id}/block-editor AJAX 호출로
 *    블록 에디터 영역 HTML 을 교체 (신규 생성 시에는 template slug 변경만 안내).
 */
import { Logger } from "../../utils/Logger";
import { Modal } from "../../utils/Modal";
import { AdminIndexTable } from "../components/AdminIndexTable";
import { WorkCanvasPreviewPanel } from "../work/WorkCanvasPreviewPanel";
import { QuillEditor } from "../../components/QuillEditor";
import { initHeroVisualColorPicker } from "../work/initHeroVisualColorPicker";

const CTRL_TAG = "AdminWorkController";

function bindTemplateSwitcher(scope: HTMLElement): void {
    const sel = scope.querySelector<HTMLSelectElement>("[data-work-template-select]");
    const mount = scope.querySelector<HTMLElement>("[data-work-block-editor-mount]");
    if (!sel || !mount) return;

    const baseUrl = mount.getAttribute("data-block-editor-url") ?? "";
    if (!baseUrl) return;

    let lastValue = sel.value;
    sel.addEventListener("change", async () => {
        const newValue = sel.value;
        if (newValue === lastValue) return;

        const ok = await Modal.confirm(
            "템플릿을 변경하면 기존 입력값이 새 템플릿 블록과 매칭되지 않을 수 있습니다. 진행할까요?",
        );
        if (!ok) {
            sel.value = lastValue;
            return;
        }

        try {
            const url = baseUrl + (baseUrl.includes("?") ? "&" : "?") + "template_id=" + encodeURIComponent(newValue);
            const res = await fetch(url, {
                headers: {
                    Accept: "text/html",
                    "X-Requested-With": "XMLHttpRequest",
                },
            });
            if (!res.ok) throw new Error("HTTP " + res.status);
            const html = await res.text();
            mount.innerHTML = html;
            lastValue = newValue;
        } catch (e) {
            Logger.error("Failed to swap block editor", e);
            void Modal.alert("블록 에디터를 불러오지 못했습니다.");
            sel.value = lastValue;
        }
    });
}

export const AdminWorkController = {
    index(): void {
        Logger.debug(`${CTRL_TAG}.index`);
        new AdminIndexTable().init();
    },
    create(): void {
        Logger.debug(`${CTRL_TAG}.create`);
        const root = document.querySelector<HTMLElement>(".admin-work-form");
        if (root) {
            bindTemplateSwitcher(root);
            initWorkDescriptionEditor(root);
            initHeroVisualColorPicker(root);
        }
    },
    edit(): void {
        Logger.debug(`${CTRL_TAG}.edit`);
        const root = document.querySelector<HTMLElement>(".admin-work-form");
        if (root) {
            bindTemplateSwitcher(root);
            initWorkDescriptionEditor(root);
            initHeroVisualColorPicker(root);
        }
    },
    canvas(): void {
        Logger.debug(`${CTRL_TAG}.canvas`);
        const root = document.querySelector<HTMLElement>(".admin-work--canvas");
        if (!root) return;
        new WorkCanvasPreviewPanel(root).init();
    },
    store(): void { Logger.debug(`${CTRL_TAG}.store (no-op)`); },
    update(): void { Logger.debug(`${CTRL_TAG}.update (no-op)`); },
    destroy(): void { Logger.debug(`${CTRL_TAG}.destroy (no-op)`); },
    blockEditor(): void { Logger.debug(`${CTRL_TAG}.blockEditor (no-op)`); },
};

function initWorkDescriptionEditor(scope: HTMLElement): void {
    const editorWrap = scope.querySelector<HTMLElement>("[data-work-desc-editor]");
    const hidden = scope.querySelector<HTMLInputElement>('input[name="description"]');
    if (!editorWrap || !hidden) return;
    if ((editorWrap as unknown as { _quillInited?: boolean })._quillInited) return;
    (editorWrap as unknown as { _quillInited?: boolean })._quillInited = true;

    const initial = editorWrap.getAttribute("data-initial-html") ?? hidden.value ?? "";
    const quill = new QuillEditor(editorWrap, {
        placeholder: "설명을 입력하세요.",
        initialHtml: initial,
        // Work 설명은 이미지 삽입 없이 텍스트 서식만.
        toolbar: [
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link"],
            ["clean"],
        ],
    });
    quill.init();

    // sync to hidden input on blur
    editorWrap.addEventListener("focusout", () => {
        hidden.value = quill.getHtml();
    });
}
